/**
 * Enhanced Catalog Page - Simplified Grid View
 * Version 4.0 - Removed Filters, Sort, View Toggle - Always Grid View
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '../../constants/routePaths';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  Chip, 
  Card, 
  Button, 
  Grid,
  CardContent,
  Fade,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Rocket as RocketIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';

import { useModelSearch } from '../../hooks/useModelSearch';
import { Model } from '../../models/Model';
import { configService } from '../../services/ConfigService';
import { deploymentInstanceService } from '../../services/DeploymentInstanceService';
import { useModels } from '../../hooks/useModels';

export function CatalogPage() {
  const { models, loading, error } = useModels();
  
  const navigate = useNavigate();
  const { searchQuery, setSearchQuery, filteredModels } = useModelSearch(models);
  
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);

  const [deployedConfigs, setDeployedConfigs] = useState<any[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(loading);

  const handleViewDetails = (modelId: string) => {
    navigate(ROUTE_PATHS.MODEL_DETAIL(modelId));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const getConfig = await configService.getConfig();
      setDeployedConfigs(getConfig);
    } catch (error) {
      console.error('Error fetching deployment instances:', error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const deployModel = async (id: string) => {
    try {
      setIsDeleting(true);
      const deploymentInstance = await deploymentInstanceService.deploymentInstance(id);      
      console.log('Deployment Instance:', deploymentInstance);
      setDeleteDialogOpen(false);
      setSelectedModel(null);
      toast.success(`"Deployed successfully"`, {
        duration: 4000,
      });
    } catch (error: any) {
      console.error('Error fetching deployment instances:', error.response.data.detail);
      toast.error(`"${error.response.data.detail}"`, {
        duration: 4000,
      });
      setDeleteDialogOpen(false);
    }  finally {
      setIsDeleting(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'deployed':
        return { 
          color: 'success' as const, 
          bgcolor: 'rgba(46, 125, 50, 0.12)', 
          borderColor: 'rgba(46, 125, 50, 0.4)',
          textColor: '#2e7d32',
          icon: <CheckCircleIcon sx={{ fontSize: 16 }} />
        };
      case 'ready':
        return { 
          color: 'info' as const, 
          bgcolor: 'rgba(2, 136, 209, 0.12)', 
          borderColor: 'rgba(2, 136, 209, 0.4)',
          textColor: '#0288d1',
          icon: <SpeedIcon sx={{ fontSize: 16 }} />
        };
      case 'draft':
        return { 
          color: 'warning' as const, 
          bgcolor: 'rgba(237, 108, 2, 0.12)', 
          borderColor: 'rgba(237, 108, 2, 0.4)',
          textColor: '#ed6c02',
          icon: <TrendingUpIcon sx={{ fontSize: 16 }} />
        };
      default:
        return { 
          color: 'default' as const, 
          bgcolor: 'rgba(0, 0, 0, 0.06)', 
          borderColor: 'rgba(0, 0, 0, 0.15)',
          textColor: '#616161',
          icon: null
        };
    }
  };

  const deployModelInstance = async (model: any) => {
    try {
      const result = deployedConfigs.filter((config) => {
        return config.model_id === model.id;
      })
      if (result.length) {
        deployModel(result[0].deployment_config_id);
      }
    } catch (error) {
      console.error('Error fetching deployment instances:', error);
    }
  };

  // Stats calculations
  const stats = useMemo(() => ({
    total: models.length,
    deployed: models.filter(m => m.status === 'deployed').length,
    providers: new Set(models.map(m => m.provider)).size,
  }), [models]);

  const renderCard = (model: Model) => {
    const statusConfig = getStatusConfig(model.status);

    return (
      <Box
        key={model.id} 
        sx={{ 
          width: { xs: '100%', sm: 'calc(33.333333% - 16px)' },
          flexGrow: 0,
          flexShrink: 0
        }}
      >
        <Fade in timeout={300}>
          <Card
            elevation={0}
            sx={{
              width: '100%',
              height: '100%',
              minHeight: 400,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2.5,
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(37, 99, 235, 0.15)',
                transform: 'translateY(-6px)',
                borderColor: 'primary.main',
                '& .model-icon': {
                  transform: 'scale(1.08) rotate(-3deg)'
                }
              }
            }}
            onClick={() => handleViewDetails(model.id)}
          >

            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', pt: 2.5 }}>
              {/* Header with Menu */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3, fontSize: '17px' }}>
                    {model.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '11px', bgcolor: 'grey.100', px: 1, py: 0.25, borderRadius: 1 }}>
                    {model.version}
                  </Typography>
                </Box>
              </Box>

              {/* Status Badge */}
              <Box sx={{ mb: 2.5 }}>
                <Chip
                  // icon={statusConfig.icon}
                  label={model.status.charAt(0).toUpperCase() + model.status.slice(1)}
                  size="small"
                  sx={{
                    bgcolor: statusConfig.bgcolor,
                    color: statusConfig.textColor,
                    border: `1px solid ${statusConfig.borderColor}`,
                    fontWeight: 700,
                    height: 26,
                    fontSize: '12px'
                  }}
                />
              </Box>

              {/* Description */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 3,
                  lineHeight: 1.65,
                  height: 48,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  fontSize: '14px'
                }}
              >
                {model.description}
              </Typography>

              {/* Specs Grid */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Provider
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '12px', color: 'text.primary' }}>
                        {model.provider}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Type
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '12px', color: 'text.primary' }}>
                        {model.type}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Size
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '12px', color: 'text.primary' }}>
                        {model.size}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Deploy Button */}
              <Button
                fullWidth
                variant="contained"
                startIcon={<RocketIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedModel(model);
                  setDeleteDialogOpen(true)
                }}
                disabled={model.status === 'deployed'}
                sx={{
                  mt: 'auto',
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '14px',
                  boxShadow: model.status === 'deployed' ? 'none' : '0 4px 12px rgba(37, 99, 235, 0.3)',
                  transition: 'all 0.2s'
                }}
              >
                {model.status === 'deployed' ? 'Deployed' : 'Deploy Model'}
              </Button>
            </CardContent>
          </Card>
        </Fade>
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: '1800px', margin: '0 auto', py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.5px' }}>
            Model Catalog
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, mb: 2 }}>
          {[
            { label: 'Total Models', value: stats.total },
            { label: 'Providers', value: stats.providers }
          ].map((stat, index) => (
            <Box key={index} sx={{ width: { xs: '100%', sm: 'calc(33.333333% - 14px)' }, flexGrow: 0, flexShrink: 0 }}>

                  {stat.label} &nbsp;&nbsp;

                  {stat.value}
            </Box>
          ))}
        </Box>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search models by name, provider, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'primary.main', fontSize: 22 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              fontSize: '15px',
              border: '1px solid',
              borderColor: 'divider',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'primary.main' },
              '&.Mui-focused': {
                bgcolor: 'white',
                '& fieldset': { borderColor: 'primary.main', borderWidth: 2 }
              }
            }
          }}
        />

        {/* Results count */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            Showing {filteredModels.length} {filteredModels.length === 1 ? 'model' : 'models'}
          </Typography>
        </Box>

        {/* Models Grid */}
        {filteredModels.length === 0 ? (
          <Paper elevation={0} sx={{ py: 12, textAlign: 'center', borderRadius: 2.5, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
              No models found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Try adjusting your search query
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, margin: 0 }}>
            {filteredModels.map(model => renderCard(model))}
          </Box>
        )}

        
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <Box>
          <DialogTitle>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              Create Deployment
            </Typography>
          </DialogTitle>
        </Box>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748b' }}>
            Are you sure you want to create a new Instance?
          </DialogContentText>
         
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              deployModelInstance(selectedModel);
            }}
            disabled={isDeleting}
            variant="contained"
            // startIcon={isDeleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#ef4444',
              '&:hover': {
                bgcolor: '#dc2626'
              },
              '&:disabled': {
                bgcolor: '#fca5a5',
                color: 'white'
              }
            }}
          >
            {isDeleting ? 'Deploying...' : 'Deploy'}
          </Button>
        </DialogActions>
        
      </Dialog>
      
    </Box>
  );
}